package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.CanvasCommentThreadDto
import dev.lingvo.shared.CanvasMentionDto
import dev.lingvo.shared.CanvasSlotMarkDto
import dev.lingvo.shared.CanvasSlotMarkState
import dev.lingvo.shared.RenderDto
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/** Untrusted comment text always enters a DOM text node, including author and mention labels. */
@Composable
internal fun CanvasReviewThreads(
    threads: List<CanvasCommentThreadDto>, members: List<CanvasMentionDto>, writable: Boolean, busy: Boolean,
    onPost: (CanvasCommentThreadDto?, String, List<String>, () -> Unit) -> Unit,
    onState: (CanvasCommentThreadDto) -> Unit,
) {
    var replyTo by remember { mutableStateOf<String?>(null) }
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        if (threads.isEmpty()) LMuted(t("app.projects.slug.screenshots.canvas_review_threads.no_discussion_on_this_selection_yet", "No discussion on this selection yet."))
        threads.forEach { thread -> key(thread.id) {
            Div(Modifier.styleModifier { property("border-inline-start", "2px solid currentColor"); property("padding-inline-start", "12px"); property("overflow-wrap", "anywhere") }.toAttrs {
                attr("data-canvas-thread", thread.id)
            }) {
                LMuted(if (thread.resolved) t("app.projects.slug.screenshots.canvas_review_threads.resolved", "Resolved") else t("app.projects.slug.screenshots.canvas_review_threads.open_discussion", "Open discussion"))
                if (thread.layerId != null) LMuted(tf("screenshots.collaboration.layer", "Layer: {layer}", "layer" to thread.layerId))
                thread.comments.forEach { comment ->
                    LMuted(comment.authorLabel)
                    P(attrs = { attr("data-canvas-comment", ""); style { property("white-space", "pre-wrap"); property("overflow-wrap", "anywhere") } }) { Text(comment.body) }
                    if (comment.mentions.isNotEmpty()) LMuted(comment.mentions.joinToString(", ") { "@${it.label}" })
                }
                Row(Modifier.gap(Space.SM).styleModifier { property("flex-wrap", "wrap") }) {
                    LButton(if (thread.resolved) t("app.projects.slug.screenshots.canvas_review_threads.reopen_discussion", "Reopen discussion") else t("app.projects.slug.screenshots.canvas_review_threads.resolve_discussion", "Resolve discussion"),
                        { onState(thread) }, enabled = writable, variant = LButtonVariant.SECONDARY, modifier = reviewControlModifier)
                    if (!thread.resolved) LButton(t("app.projects.slug.screenshots.canvas_review_threads.reply", "Reply"), { replyTo = if (replyTo == thread.id) null else thread.id },
                        enabled = writable, variant = LButtonVariant.GHOST, modifier = reviewControlModifier)
                }
                if (replyTo == thread.id && !thread.resolved) CommentComposer(members, writable, busy, reply = true) { text, mentions, done ->
                    onPost(thread, text, mentions) { done(); replyTo = null }
                }
            }
        } }
        CommentComposer(members, writable, busy, reply = false) { text, mentions, done -> onPost(null, text, mentions, done) }
    }
}

private val reviewControlModifier = Modifier.styleModifier {
    property("min-height", "40px"); property("max-width", "100%"); property("white-space", "normal")
}

@Composable
private fun CommentComposer(
    members: List<CanvasMentionDto>, writable: Boolean, busy: Boolean, reply: Boolean,
    onSubmit: (String, List<String>, () -> Unit) -> Unit,
) {
    var body by remember { mutableStateOf("") }
    var mentions by remember { mutableStateOf<List<String>>(emptyList()) }
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        LTextArea(body, { body = it.take(4000) }, label = if (reply) t("app.projects.slug.screenshots.canvas_review_threads.reply_text", "Reply text") else t("app.projects.slug.screenshots.canvas_review_threads.comment_on_this_selection", "Comment on this selection"),
            enabled = writable, modifier = Modifier.fillMaxWidth())
        LSelect("", listOf(LSelectOption("", t("app.projects.slug.screenshots.canvas_review_threads.choose_a_workspace_member", "Choose a workspace member"))) +
            members.filter { it.userId !in mentions }.map { LSelectOption(it.userId, it.label) },
            { id -> if (id.isNotBlank() && mentions.size < 10 && members.any { it.userId == id }) mentions = mentions + id },
            label = t("app.projects.slug.screenshots.canvas_review_threads.mention_a_workspace_member_optional", "Mention a workspace member (optional)"), enabled = writable && mentions.size < 10,
            modifier = Modifier.fillMaxWidth().styleModifier { property("min-width", "0") })
        Row(Modifier.gap(Space.SM).styleModifier { property("flex-wrap", "wrap") }) {
            members.filter { it.userId in mentions }.forEach { member ->
                LButton(tf("screenshots.collaboration.remove_mention", "Remove mention: {name}", "name" to member.label), { mentions = mentions - member.userId },
                    enabled = writable, variant = LButtonVariant.GHOST, modifier = reviewControlModifier)
            }
        }
        LButton(if (reply) t("app.projects.slug.screenshots.canvas_review_threads.post_reply", "Post reply") else t("app.projects.slug.screenshots.canvas_review_threads.post_comment", "Post comment"),
            { if (writable && body.isNotBlank()) onSubmit(body, mentions) { body = ""; mentions = emptyList() } },
            enabled = writable && body.isNotBlank(), loading = busy, variant = LButtonVariant.SECONDARY, modifier = reviewControlModifier)
    }
}

/** Individual marks remain visible beside the image and never invoke the complete-set approval API. */
@Composable
internal fun CanvasReviewMarks(
    marks: List<CanvasSlotMarkDto>, render: RenderDto?, writable: Boolean, busy: Boolean, onMark: (CanvasSlotMarkState) -> Unit,
) {
    Div(attrs = { attr("data-canvas-marks", ""); style { property("margin-top", "20px"); property("overflow-wrap", "anywhere") } }) {
        LMuted(t("app.projects.slug.screenshots.canvas_review_threads.persons_marks_for_this_screenshot_and", "Person's marks for this screenshot and device"))
        marks.forEach { mark ->
            val status = if (mark.state == CanvasSlotMarkState.APPROVED) t("app.projects.slug.screenshots.canvas_review_threads.approved_screenshot", "Approved screenshot")
                else t("app.projects.slug.screenshots.canvas_review_threads.changes_requested", "Changes requested")
            LMuted(tf("screenshots.collaboration.mark_by", "{status} · {name}", "status" to status, "name" to mark.reviewerLabel))
            if (!mark.valid) LMuted(t("app.projects.slug.screenshots.canvas_review_threads.outdated_mark_the_image_changed_review", "This mark is outdated. The screenshot or reviewer access may have changed. Review it again before marking."))
        }
        if (render == null) LMuted(t("app.projects.slug.screenshots.canvas_review_threads.compose_this_saved_version_before_marking", "Compose this saved version before marking its screenshot."))
        Row(Modifier.gap(Space.SM).styleModifier { property("flex-wrap", "wrap") }) {
            LButton(t("app.projects.slug.screenshots.canvas_review_threads.mark_screenshot_approved", "Mark screenshot approved"), { onMark(CanvasSlotMarkState.APPROVED) },
                enabled = writable && render != null, loading = busy, variant = LButtonVariant.SECONDARY, modifier = reviewControlModifier)
            LButton(t("app.projects.slug.screenshots.canvas_review_threads.request_screenshot_changes", "Request screenshot changes"), { onMark(CanvasSlotMarkState.CHANGES_REQUESTED) },
                enabled = writable && render != null, loading = busy, variant = LButtonVariant.GHOST, modifier = reviewControlModifier)
        }
    }
}
